import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";

const STATUS_STYLE = {
  ACTIVE: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  DRAFT: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  SUPERSEDED: "bg-[#F1F5F9] text-[#334155] border-[#E2E8F0]",
};

function VersionRow({ v, area, isAdmin, busy, act }) {
  const { t } = useLang();
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs border rounded-md px-2.5 py-1.5"
      data-testid={`config-version-${v.id}`}>
      <span className="font-medium">v{v.version}</span>
      <span className={`border rounded px-1.5 py-0.5 font-medium ${STATUS_STYLE[v.status] || ""}`}>{v.status}</span>
      <span className="text-muted-foreground truncate max-w-[240px]">
        {v.change_summary || "—"} · {v.created_by_name}
      </span>
      {v.validation_errors && <span className="text-[#991B1B]">{v.validation_errors.join("; ")}</span>}
      {v.validated_at && v.status === "DRAFT" && (
        <span className="text-[#166534] flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> validated</span>
      )}
      {isAdmin && (
        <span className="ml-auto flex gap-1.5">
          {v.status === "DRAFT" && !v.validated_at && (
            <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={busy}
              data-testid={`config-validate-${v.id}`}
              onClick={() => act(() => api.post(`/admin/configurations/${v.id}/validate`), t("validate") + " ✓")}>
              {t("validate")}
            </Button>
          )}
          {v.status === "DRAFT" && v.validated_at && (
            <Button size="sm" className="h-6 text-[11px] bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={busy}
              data-testid={`config-activate-${v.id}`}
              onClick={() => act(() => api.post(`/admin/configurations/${v.id}/activate`), t("activate") + " ✓")}>
              <CheckCircle2 className="h-3 w-3 mr-1" /> {t("activate")}
            </Button>
          )}
          {v.status === "SUPERSEDED" && (
            <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={busy}
              data-testid={`config-rollback-${v.id}`}
              onClick={() => act(() => api.post(`/admin/configurations/${v.id}/rollback`), t("rollback") + " ✓")}>
              <RotateCcw className="h-3 w-3 mr-1" /> {t("rollback")}
            </Button>
          )}
        </span>
      )}
    </div>
  );
}

function AreaCard({ area, isAdmin, onChanged }) {
  const { t } = useLang();
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState({});
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);

  const startDraft = () => {
    setValues({ ...(area.active?.value_json || area.defaults) });
    setSummary("");
    setEditing(true);
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.valid === false) {
        toast.error(r.data.errors.join("; "));
      } else {
        toast.success(msg);
        onChanged();
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = () =>
    act(() => api.post("/admin/configurations", {
      config_area: area.area,
      value_json: Object.fromEntries(Object.entries(values).map(([k, v]) => [k, Number(v)])),
      change_summary: summary || null,
    }).then((r) => { setEditing(false); return r; }), t("draftSaved"));

  const drafts = area.versions.filter((v) => v.status === "DRAFT");
  const superseded = area.versions.filter((v) => v.status === "SUPERSEDED");

  return (
    <div className="apha-card p-5 space-y-4" data-testid={`config-area-${area.area}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading font-semibold">{area.label}</h3>
          <div className="text-xs text-muted-foreground mt-0.5">
            {t("activeConfig")}: v{area.active?.version ?? "—"}
            {area.active?.activated_at && ` · ${new Date(area.active.activated_at).toLocaleString()}`}
            {area.active?.activated_by_name && ` · ${area.active.activated_by_name}`}
          </div>
        </div>
        {isAdmin && !editing && (
          <Button size="sm" variant="outline" data-testid={`config-new-draft-${area.area}`} onClick={startDraft}>
            {t("createDraft")}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Object.entries(editing ? values : (area.active?.value_json || area.defaults)).map(([k, v]) => (
          <div key={k} className="space-y-1">
            <Label className="text-[11px] leading-tight" title={area.hints?.[k]}>{k}</Label>
            <Input
              data-testid={`config-input-${area.area}-${k}`}
              type="number" step={area.area === "health_weights" ? "0.05" : "1"}
              value={editing ? values[k] : v} disabled={!editing}
              onChange={(e) => setValues({ ...values, [k]: e.target.value })}
            />
          </div>
        ))}
      </div>

      {editing && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1 flex-1 min-w-[200px]">
            <Label className="text-xs">{t("changeSummary")}</Label>
            <Input data-testid={`config-summary-${area.area}`} value={summary} onChange={(e) => setSummary(e.target.value)} />
          </div>
          <Button size="sm" data-testid={`config-save-draft-${area.area}`} className="bg-[#1E3A8A] hover:bg-[#1c3479]"
            disabled={busy} onClick={saveDraft}>{t("saveDraft")}</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>✕</Button>
        </div>
      )}

      {(drafts.length > 0 || superseded.length > 0) && (
        <div className="space-y-1.5">
          <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{t("versionHistory")}</div>
          {[...drafts, ...superseded].map((v) => (
            <VersionRow key={v.id} v={v} area={area} isAdmin={isAdmin} busy={busy} act={act} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminConfig({ isAdmin }) {
  const { t } = useLang();
  const [areas, setAreas] = useState(null);
  const load = useCallback(() =>
    api.get("/admin/configurations").then((r) => setAreas(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!areas) return <Skeleton className="h-60 w-full" />;
  return (
    <div className="space-y-5" data-testid="admin-config-tab">
      <p className="text-sm text-muted-foreground">{t("configIntro")}</p>
      {areas.map((a) => <AreaCard key={a.area} area={a} isAdmin={isAdmin} onChanged={load} />)}
    </div>
  );
}
